import React, { useEffect, useState } from 'react'
import { Chip, Checkbox, Button, Popover, PopoverTrigger, PopoverContent } from '@nextui-org/react'
import { Icon } from '@iconify/react'
import toast from 'react-hot-toast'
import { useSocketContextData } from '../../../context/socket'

interface Requirement {
  id: string
  type: string
  requirement: string
  status: 'mandatory' | 'preferred'
  details: string
  completed: boolean
  isCompliant?: boolean
  aiAnalysis?: {
    aiReason?: string
    confidence?: number
    evidence?: string[]
  }
}

interface RequirementsProps {
  tender: any
  loadTender: () => void
}

const Requirements: React.FC<RequirementsProps> = ({ tender, loadTender }) => {
  const [requirements, setRequirements] = useState<Requirement[]>([])
  const [updating, setUpdating] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [analyzingCompliance, setAnalyzingCompliance] = useState(false)
  const { io, isSocketConnected } = useSocketContextData()

  useEffect(() => {
    const reqs = tender?.requirements
      ?.map((item: string, index: number) => {
        try {
          const parsed = JSON.parse(item)
          const isCompliant = tender?.complianceStatus?.[index] || false
          const complianceDetails = tender?.complianceDetails?.[index]
          
          // Add compliance status from tender data and auto-tick compliant requirements
          return {
            ...parsed,
            isCompliant,
            completed: isCompliant, // Auto-tick compliant requirements
            aiAnalysis: complianceDetails ? {
              aiReason: complianceDetails.aiReason,
              confidence: complianceDetails.confidence,
              evidence: complianceDetails.evidence
            } : undefined
          }
        } catch (err) {
          return false
        }
      })
      .filter((item: any) => item)
    setRequirements(reqs)
  }, [tender?.requirements, tender?.complianceStatus])
  const handleRequirementToggle = async (requirementId: string, completed: boolean) => {
    if (!tender?.id) return

    // Find the requirement to check if it's compliant
    const requirement = requirements.find(req => req.id === requirementId)
    
    // If trying to uncheck a compliant requirement, show warning
    if (requirement?.isCompliant && !completed) {
      toast.error('This requirement is marked as compliant by AI analysis. Consider re-running compliance analysis if your company status has changed.')
      return
    }

    setUpdating(true)
    try {
      const updatedRequirements = requirements.map((req) => (req.id === requirementId ? { ...req, completed } : req))

      // Update local state immediately for better UX
      setRequirements(updatedRequirements)
      toast.success('Requirement updated successfully')
    } catch (error) {
      console.error('Error updating requirement:', error)
      toast.error('Failed to update requirement')

      // Revert local state on error
      setRequirements(requirements)
    } finally {
      setUpdating(false)
    }
  }

  const getTypeColor = (type: string) => {
    const colors: { [key: string]: 'default' | 'primary' | 'success' | 'warning' | 'secondary' | 'danger' } = {
      Technical: 'primary',
      Certification: 'success',
      Financial: 'warning',
      Experience: 'secondary',
      Legal: 'danger',
      Insurance: 'default',
      Other: 'default',
    }
    return colors[type] || 'default'
  }

  const getStatusColor = (status: string) => {
    return status === 'mandatory' ? 'danger' : 'warning'
  }

  const getStatusIcon = (status: string) => {
    return status === 'mandatory' ? 'lucide:alert-circle' : 'lucide:info'
  }

  const handleAnalyzeTender = async () => {
    if (!tender?.id || !isSocketConnected) {
      toast.error('Unable to connect to server')
      return
    }

    setAnalyzing(true)
    try {
      // Set up socket listeners
      const requirementsGeneratedListener = (data: any) => {
        if (data.tenderId === tender.id) {
          toast.dismiss()
          toast.success(data.message || 'Requirements generated successfully! Starting compliance analysis...')
          // Keep analyzing state true for compliance analysis
        }
      }

      const requirementsAndComplianceGeneratedListener = (data: any) => {
        if (data.tenderId === tender.id) {
          toast.dismiss()
          toast.success(data.message || 'Requirements and compliance analysis completed!')
          setAnalyzing(false)
          // Reload tender data to get updated requirements and compliance
          loadTender()
        }
      }

      const requirementsGeneratedFinalListener = (data: any) => {
        if (data.tenderId === tender.id) {
          toast.dismiss()
          toast.success(data.message || 'Requirements generated successfully!')
          setAnalyzing(false)
          // Reload tender data to get updated requirements
          loadTender()
        }
      }

      const errorListener = (data: any) => {
        if (data.tenderId === tender.id) {
          toast.dismiss()
          toast.error(data.message || 'Failed to generate requirements')
          setAnalyzing(false)
        }
      }

      const progressListener = (data: any) => {
        if (data.tenderId === tender.id) {
          toast.dismiss()
          toast(data.message, { icon: '⏳' })
        }
      }

      io.on('requirements_generated', requirementsGeneratedListener)
      io.on('requirements_and_compliance_generated', requirementsAndComplianceGeneratedListener)
      io.on('requirements_generated_final', requirementsGeneratedFinalListener)
      io.on('compliance_progress', progressListener) // Reuse progress listener for compliance updates
      io.on('requirements_error', errorListener)
      io.on('requirements_progress', progressListener)

      // Emit the analyze request
      io.emit('analyze_tender_requirements', {
        tenderId: tender.id,
        token: localStorage.getItem('accessToken'),
      })

      // Set a timeout to clean up listeners
      setTimeout(() => {
        io.off('requirements_generated', requirementsGeneratedListener)
        io.off('requirements_and_compliance_generated', requirementsAndComplianceGeneratedListener)
        io.off('requirements_generated_final', requirementsGeneratedFinalListener)
        io.off('compliance_progress', progressListener)
        io.off('requirements_error', errorListener)
        io.off('requirements_progress', progressListener)
        if (analyzing) {
          setAnalyzing(false)
          toast.error('Request timed out. Please try again.')
        }
      }, 300000) // 5 minute timeout for requirements + compliance analysis
    } catch (error) {
      console.error('Error analyzing tender:', error)
      toast.error('Failed to start analysis')
      setAnalyzing(false)
    }
  }

  const handleAnalyzeCompliance = async () => {
    if (!tender?.id || !isSocketConnected) {
      toast.error('Unable to connect to server')
      return
    }

    if (!requirements || requirements.length === 0) {
      toast.error('Please generate tender requirements first')
      return
    }

    setAnalyzingCompliance(true)
    try {
      // Set up socket listeners
      const complianceGeneratedListener = (data: any) => {
        if (data.tenderId === tender.id) {
          toast.dismiss()
          toast.success('Compliance analysis completed! Compliant requirements have been automatically checked.')
          setAnalyzingCompliance(false)
          // Reload tender data to get updated compliance status
          loadTender()
        }
      }

      const errorListener = (data: any) => {
        if (data.tenderId === tender.id) {
          toast.dismiss()
          toast.error(data.message || 'Failed to analyze compliance')
          setAnalyzingCompliance(false)
        }
      }

      const progressListener = (data: any) => {
        if (data.tenderId === tender.id) {
          toast.dismiss()
          toast(data.message, { icon: '⏳' })
        }
      }

      io.on('compliance_generated', complianceGeneratedListener)
      io.on('compliance_error', errorListener)
      io.on('compliance_progress', progressListener)

      // Emit the analyze request
      io.emit('analyze_compliance', {
        tenderId: tender.id,
        token: localStorage.getItem('accessToken'),
      })

      // Set a timeout to clean up listeners
      setTimeout(() => {
        io.off('compliance_generated', complianceGeneratedListener)
        io.off('compliance_error', errorListener)
        io.off('compliance_progress', progressListener)
        if (analyzingCompliance) {
          setAnalyzingCompliance(false)
          toast.error('Request timed out. Please try again.')
        }
      }, 180000) // 3 minute timeout for compliance analysis

    } catch (error) {
      console.error('Error analyzing compliance:', error)
      toast.error('Failed to start compliance analysis')
      setAnalyzingCompliance(false)
    }
  }

  if (!requirements || requirements.length === 0) {
    return (
      <div className="w-full text-center py-12">
        <Icon icon="lucide:clipboard-list" className="mx-auto mb-4" width={48} color="#71717a" />
        <h3 className="text-lg font-semibold text-default-600 mb-2">No Requirements Available</h3>
        <p className="text-default-400 mb-6">This tender doesn&apos;t have any requirements defined yet.</p>

        <Button
          color="primary"
          variant="flat"
          size="lg"
          startContent={<Icon icon="lucide:brain" width={20} />}
          onClick={handleAnalyzeTender}
          isLoading={analyzing}
          isDisabled={!isSocketConnected}
          className="min-w-48"
        >
          {analyzing ? 'Analyzing Tender...' : 'Analyse Tender Requirements'}
        </Button>

        {analyzing && (
          <div className="mt-4 text-sm text-default-500">
            <p>AI is analyzing the tender content and attachments...</p>
            <p>This may take a few minutes depending on the complexity.</p>
          </div>
        )}

        {!isSocketConnected && <p className="mt-4 text-sm text-warning-600">Connecting to server...</p>}
      </div>
    )
  }

  return (
    <div className="space-y-4 pt-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-default-900">Tender Requirements</h2>
        <div className="flex items-center gap-4">
          {tender?.hasCompliance && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-default-500">
                {requirements.filter((r) => r.isCompliant).length} of {requirements.length} compliant
              </span>
              <Chip
                size="sm"
                variant="flat"
                color={requirements.filter((r) => r.isCompliant).length === requirements.length ? 'success' : 'warning'}
              >
                {Math.round((requirements.filter((r) => r.isCompliant).length / requirements.length) * 100)}% Compliant
              </Chip>
            </div>
          )}
        </div>
      </div>

      {!tender?.hasCompliance && (
        <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon icon="lucide:shield-check" width={20} className="text-warning-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-warning-700">Compliance Analysis Available</p>
              <p className="text-xs text-warning-600 mt-1">
                Analyze your company profile against these requirements to check compliance automatically.
              </p>
            </div>
            <Button
              color="warning"
              variant="flat"
              size="sm"
              startContent={<Icon icon="lucide:shield-check" width={16} />}
              onClick={handleAnalyzeCompliance}
              isLoading={analyzingCompliance}
              isDisabled={!isSocketConnected}
            >
              {analyzingCompliance ? 'Analyzing...' : 'Analyze Compliance'}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {requirements.map((requirement) => {
          return (
            <div key={requirement.id} className="w-full p-4 border border-default-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Checkbox
                  isSelected={requirement.completed}
                  onValueChange={(checked) => handleRequirementToggle(requirement.id, checked)}
                  isDisabled
                  className="mt-1"
                  color={requirement.isCompliant ? 'success' : 'default'}
                />

                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Chip size="sm" variant="flat" color={getTypeColor(requirement.type)}>
                      {requirement.type}
                    </Chip>

                    <Chip
                      size="sm"
                      variant="flat"
                      color={getStatusColor(requirement.status)}
                      startContent={<Icon icon={getStatusIcon(requirement.status)} width={14} />}
                    >
                      {requirement.status}
                    </Chip>

                    {tender?.hasCompliance && requirement.aiAnalysis && (
                      <Popover placement="top">
                        <PopoverTrigger>
                          <Chip
                            size="sm"
                            variant="flat"
                            color={requirement.isCompliant ? 'success' : 'danger'}
                            startContent={<Icon icon={requirement.isCompliant ? 'lucide:check-circle' : 'lucide:x-circle'} width={14} />}
                            className="cursor-pointer hover:opacity-80"
                          >
                            {requirement.isCompliant ? 'Compliant (Auto-checked)' : 'Non-Compliant'}
                            {requirement.aiAnalysis.confidence && (
                              <span className="ml-1 text-xs opacity-70">
                                {requirement.aiAnalysis.confidence}%
                              </span>
                            )}
                          </Chip>
                        </PopoverTrigger>
                        <PopoverContent className="p-4 max-w-sm">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <Icon icon="lucide:brain" width={16} className="text-primary" />
                              <span className="font-medium text-sm">AI Analysis</span>
                              {requirement.aiAnalysis.confidence && (
                                <Chip size="sm" variant="flat" color="default">
                                  {requirement.aiAnalysis.confidence}% confidence
                                </Chip>
                              )}
                            </div>
                            
                            {requirement.aiAnalysis.aiReason && (
                              <div>
                                <p className="text-xs font-medium text-default-600 mb-1">Reasoning:</p>
                                <p className="text-xs text-default-500">{requirement.aiAnalysis.aiReason}</p>
                              </div>
                            )}
                            
                            {requirement.aiAnalysis.evidence && requirement.aiAnalysis.evidence.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-default-600 mb-2">Evidence Found:</p>
                                <div className="flex flex-wrap gap-1">
                                  {requirement.aiAnalysis.evidence.map((evidence: string, idx: number) => (
                                    <Chip key={idx} size="sm" variant="flat" color="primary" className="text-xs">
                                      {evidence}
                                    </Chip>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                    
                    {tender?.hasCompliance && !requirement.aiAnalysis && (
                      <Chip
                        size="sm"
                        variant="flat"
                        color={requirement.isCompliant ? 'success' : 'danger'}
                        startContent={<Icon icon={requirement.isCompliant ? 'lucide:check-circle' : 'lucide:x-circle'} width={14} />}
                      >
                        {requirement.isCompliant ? 'Compliant (Auto-checked)' : 'Non-Compliant'}
                      </Chip>
                    )}
                  </div>

                  <div className="space-y-1">
                    <p
                      className={`font-medium ${
                        requirement.completed ? 'line-through text-default-400' : 'text-default-900'
                      }`}
                    >
                      {requirement.requirement}
                    </p>

                    {requirement.details && (
                      <p className={`text-sm text-default-500 ${requirement.completed ? 'line-through' : ''}`}>
                        {requirement.details}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {requirements.filter((r) => r.completed).length === requirements.length && (
        <div className="w-full bg-success-50 border border-success-200 rounded-lg text-center py-6">
          <Icon icon="lucide:check-circle" className="mx-auto mb-2" width={32} color="#17c964" />
          <h3 className="text-lg font-semibold text-success-700 mb-1">All Requirements Met!</h3>
          <p className="text-success-600">Congratulations! Your company meets all the requirements for this tender.</p>
        </div>
      )}
    </div>
  )
}

export default Requirements
